import { BACKUP_DEBOUNCE_MS, BOT_API_URL } from './config.js';
import { AuthError, botFetch, botJson, getAuthToken, jsonRequestBody } from './api.js';

/**
 * Mengelola backup otomatis "index" (file tree) Disbox.
 *
 * Index (nama file, folder, ID chunk di Discord) disimpan di browser
 * (localStorage atau IndexedDB, lihat tree-store.js), dan setiap kali berubah
 * dikirim ke bot (POST /backup/:userId, terkompresi gzip kalau besar,
 * wajib login) yang menyimpannya sebagai backup di Discord. Dulu dikirim
 * langsung ke URL webhook yang tertanam di web app - URL itu bisa dilihat
 * siapa pun yang membuka web, jadi sekarang lewat bot.
 *
 * Setiap pengiriman menyertakan `baseMessageId` (versi backup yang menjadi
 * dasar perubahan di perangkat ini). Kalau sejak itu perangkat lain / WebDAV
 * sudah menyimpan versi baru, bot MENGGABUNGKAN keduanya dan mengembalikan
 * hasil gabungannya - tidak ada lagi perubahan yang saling menimpa.
 *
 * Kalau bot sedang tidak bisa dihubungi, pengiriman diulang otomatis (jeda
 * makin lama), dan perubahannya tetap aman di perangkat ini (ditandai
 * "belum tersimpan") sampai berhasil terkirim.
 */
export default class IndexBackupManager {
    // handlers: { getState() -> { tree, version, baseMessageId }, onSaved(result), onStatus?(status, error) }
    constructor(userId, handlers) {
        this.userId = userId;
        this.handlers = handlers;
        this._timer = null;
        this._inflight = null;
        this._pending = false;
        this._retryDelay = 0;
        this._registerUnloadFlush();
    }

    schedule(delay = BACKUP_DEBOUNCE_MS) {
        this._pending = true;
        clearTimeout(this._timer);
        this._timer = setTimeout(() => {
            this._timer = null;
            this.flush();
        }, delay);
    }

    hasPending() {
        return this._pending || Boolean(this._inflight);
    }

    // Kirim sekarang juga (tanpa menunggu jeda). Mengembalikan setelah selesai.
    async flushNow() {
        clearTimeout(this._timer);
        this._timer = null;
        this._pending = true;
        if (this._inflight) await this._inflight.catch(() => {});
        return this.flush();
    }

    async flush() {
        if (this._inflight) {
            this._pending = true;
            return this._inflight;
        }
        if (!this._pending) return undefined;
        this._pending = false;
        const { tree, version, baseMessageId } = this.handlers.getState();
        this._inflight = (async () => {
            try {
                const { body, headers } = await jsonRequestBody({ index: tree, baseMessageId: baseMessageId || null });
                const result = await botJson(`/backup/${this.userId}`, {
                    method: 'POST',
                    body,
                    headers,
                    // Index besar (ratusan ribu file) butuh waktu untuk dikirim & digabung.
                    timeoutMs: 10 * 60 * 1000,
                });
                this._retryDelay = 0;
                this.handlers.onSaved({ ...result, version });
                if (this.handlers.onStatus) this.handlers.onStatus('saved');
            } catch (error) {
                if (error instanceof AuthError) {
                    if (this.handlers.onStatus) this.handlers.onStatus('auth', error);
                    return;
                }
                this._pending = true;
                this._retryDelay = Math.min(this._retryDelay ? this._retryDelay * 2 : 5000, 5 * 60 * 1000);
                console.warn(`[Disbox] Gagal menyimpan index ke bot, dicoba lagi dalam ${Math.round(this._retryDelay / 1000)} detik:`, error.message);
                if (this.handlers.onStatus) this.handlers.onStatus('error', error);
                clearTimeout(this._timer);
                this._timer = setTimeout(() => {
                    this._timer = null;
                    this.flush();
                }, this._retryDelay);
            }
        })();
        try {
            await this._inflight;
        } finally {
            this._inflight = null;
        }
        // Ada perubahan baru selama menunggu balasan: jadwalkan pengiriman berikutnya.
        if (this._pending && !this._timer) this.schedule(1000);
        return undefined;
    }

    // Kalau tab/browser ditutup SEBELUM jeda di atas selesai, coba kirim lewat
    // navigator.sendBeacon (tetap terkirim walau halaman sudah ditinggalkan).
    // Batas sendBeacon sekitar 64KB, jadi index besar tidak bisa lewat sini -
    // tidak masalah: perubahan tetap ditandai "belum tersimpan" di perangkat
    // ini dan dikirim otomatis saat app dibuka lagi.
    _registerUnloadFlush() {
        if (typeof document === 'undefined' || typeof navigator === 'undefined' || !navigator.sendBeacon) {
            return;
        }
        const flush = () => {
            if (!this._pending || !getAuthToken()) return;
            try {
                const { tree, baseMessageId } = this.handlers.getState();
                const json = JSON.stringify({ index: tree, baseMessageId: baseMessageId || null });
                if (json.length > 60000) return;
                const url = `${BOT_API_URL}/backup/${this.userId}?t=${encodeURIComponent(getAuthToken())}`;
                navigator.sendBeacon(url, new Blob([json], { type: 'text/plain' }));
            } catch {
                // Upaya terakhir saja.
            }
        };
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') flush();
        });
        window.addEventListener('pagehide', flush);
    }

    // Backup TERBARU milik akun ini di bot: { tree, timestamp, messageId }.
    async fetchLatest() {
        const response = await botFetch(`/backup/${this.userId}`, { timeoutMs: 5 * 60 * 1000 });
        if (response.status === 404) {
            const error = new Error('Belum ada backup index tersimpan untuk akun ini.');
            error.noBackup = true;
            throw error;
        }
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data.error || `Gagal mengambil backup dari bot, status ${response.status}`);
        }
        return { tree: data.index, timestamp: data.timestamp, messageId: data.messageId };
    }

    // Riwayat versi (untuk "Riwayat versi"): [{ messageId, timestamp }], terbaru dulu.
    async fetchHistory() {
        return botJson(`/backup/${this.userId}/history`);
    }

    async fetchVersion(messageId) {
        const data = await botJson(`/backup/${this.userId}/${messageId}`, { timeoutMs: 5 * 60 * 1000 });
        return { tree: data.index, timestamp: data.timestamp, messageId: data.messageId };
    }
}
